import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import OpalTile from '../../../opal/OpalTile'
import ViewLink from './tiles/ViewLink'
import SsoTile from './tiles/SsoTile'

const PREVIEW_SIZES = {
  maxHeightPx: 220,
  maxWidthPx: 280
}

const TileContent = ({
  className,
  lowlighted,
  highlighted,
  imgSrc,
  title,
  position,
  desc
}) => {
  const style = {
    top: position ? `min(calc(8px + 100% - ${PREVIEW_SIZES.maxHeightPx / 2}px), max(calc(8px + ${PREVIEW_SIZES.maxHeightPx / 2}px), calc(50% - ${position[1]}vh)))` : undefined,
    left: position ? `calc(50% + ${position[0]}vw)` : undefined,
    transform: position ? 'translate(-50%, -50%)' : undefined,
    maxWidth: PREVIEW_SIZES.maxWidthPx,
    maxHeight: PREVIEW_SIZES.maxHeightPx,
    width: PREVIEW_SIZES.maxWidthPx,
    height: PREVIEW_SIZES.maxHeightPx,
    minWidth: PREVIEW_SIZES.maxWidthPx,
    minHeight: PREVIEW_SIZES.maxHeightPx
  }
  const shouldClear = imgSrc || desc
  return (
    <OpalTile
      size='xl'
      style={style}
      className={clsx(className, 'opal-selectable clear-trigger shadow-lg transition-opacity transition-1s', {
        'opacity-lowlighted': lowlighted,
        'transition-delay-long': lowlighted,
        'opacity-100': highlighted
      })}
    >
      {title ? (
        <div
          className={clsx('absolute', { 'clear-target': shouldClear })}
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: '100'
          }}
        >
          <div className='font-size-sm btn-primary-md'>
            {title}
          </div>
        </div>
      ) : null}
      {imgSrc ? (
        <img src={imgSrc} alt={title} className='w-full h-full show-target-semi object-contain absolute top-0 left-0' />
      ) : null}
      {(!imgSrc && desc) ? (
        <div className='show-target'>
          <div className='absolute top-0 left-0 ml-2 mt-2 font-bold bg-surface-050 py-1 px-2 radius-sm opal-surface-primary shadow-sm'>{title}</div>
          <div className='p-md text-center font-medium'>{desc}</div>
        </div>
      ) : null}
      {(imgSrc && title) ? (
        <div className='show-target'>
          <div className='absolute top-0 left-0 ml-2 mt-2 font-bold bg-surface-050 py-1 px-2 radius-sm opal-surface-primary shadow-sm'>{title}</div>
        </div>
      ) : null}
    </OpalTile>
  )
}

TileContent.propTypes = {
  className: PropTypes.string,
  lowlighted: PropTypes.bool,
  highlighted: PropTypes.bool,
  imgSrc: PropTypes.string,
  theme: PropTypes.shape({}),
  title: PropTypes.string,
  desc: PropTypes.string,
  position: PropTypes.array
}

const DashboardTile = ({
  layout,
  type,
  to,
  title,
  desc,
  imgSrc,
  viewKey,
  position,
  onHoverChange,
  lowlighted,
  highlighted,
  ...restProps
}) => {
  const linkRef = useRef(null)
  const onMouse = useCallback((e) => {
    onHoverChange(viewKey, e.type === 'mouseenter')
  }, [viewKey, onHoverChange])

  useEffect(() => {
    const node = linkRef.current
    node?.addEventListener('mouseenter', onMouse)
    node?.addEventListener('mouseleave', onMouse)
    return () => {
      node?.removeEventListener('mouseenter', onMouse)
      node?.removeEventListener('mouseleave', onMouse)
      onMouse({ type: 'mouseleave' })
    }
  }, [onMouse, linkRef])

  if (type === 'sso') {
    return (
      <SsoTile
        ref={linkRef} providerId={restProps.providerId}
        sameTab={restProps.sameTab}
      >
        <TileContent
          className={layout === 'circular' ? 'absolute' : 'relative'}
          lowlighted={lowlighted}
          highlighted={highlighted}
          position={layout === 'circular' ? position : null}
          desc={desc}
          title={title}
          imgSrc={imgSrc}
        />
      </SsoTile>
    )
  }

  return (
    <ViewLink to={to} ref={linkRef}>
      <TileContent
        className={layout === 'circular' ? 'absolute' : 'relative'}
        lowlighted={lowlighted}
        highlighted={highlighted}
        position={layout === 'circular' ? position : null}
        title={title}
        desc={desc}
        imgSrc={imgSrc}
      />
    </ViewLink>
  )
}

DashboardTile.propTypes = {
  layout: PropTypes.oneOf(['linear', 'circular']),
  type: PropTypes.oneOf(['link', 'sso']),
  to: PropTypes.string,
  title: PropTypes.string,
  imgSrc: PropTypes.string,
  smallTitle: PropTypes.string,
  desc: PropTypes.string,
  viewKey: PropTypes.string,
  position: PropTypes.array,
  onHoverChange: PropTypes.func,
  lowlighted: PropTypes.bool,
  highlighted: PropTypes.bool
}

export default DashboardTile
